/* eslint-disable react/prop-types */
import React from "react";
import "../../assets/styles/App.css";
import { Button, FormGroup, FormControl } from "react-bootstrap";
import { toast } from "react-toastify";
import { updateSiteConfigurations } from "../../constants/endpoints";
import { putAuthenticatedData } from "../../utils/async";
import BootstrapSwitchButton from "bootstrap-switch-button-react";
import { withTranslation } from "react-i18next";
import { T_GENERAL, T_SERVICE, T_SITES } from "../../constants/translations";

/**
 * This is the component for the Site Entitlement Settings modal in the Sites page. <br>
 * The site ID is passed as a prop called `siteID`
 *
 * @component
 */
class CustomSiteSettingsModal extends React.Component {
  constructor(props) {
    super(props);

    this.t = props.t;
    this.i18n = props.i18n;

    let objs = Object.entries(this.props.siteEntitlements)
      .flatMap(item => item[1].flatMap(it => it.name))
      .map(i => {
        let j = Object.entries(
          this.props.configurations ? this.props.configurations : {}
        ).flatMap(some => some[1]);
        return [
          i,
          j.find(k => k.name === i)
            ? j.find(k => k.name === i).value
            : Object.entries(this.props.siteEntitlements)
                .flatMap(i => i[1])
                .find(l => l.name === i).type === "BOOLEAN"
            ? false
            : ""
        ];
      });
    let objsWithValues = Object.fromEntries(objs);

    // Handle CUSTOM_FIELDS specially
    let customFields = [];
    if (objsWithValues.CUSTOM_FIELDS) {
      try {
        customFields = JSON.parse(objsWithValues.CUSTOM_FIELDS);
      } catch (e) {
        customFields = [];
      }
    }

    this.MAX_CUSTOM_FIELDS = 8;

    this.retinaRiskRequiredFields = [
      {
        id: "diabetesType",
        label: "Diabetes type",
        type: "picker",
        required: false,
        attributeType: "patient",
        options: [
          { value: "Type1", display: "Type 1" },
          { value: "Type2", display: "Type 2" }
        ]
      },
      {
        id: "diagnosisYear",
        label: "Diagnosis year",
        type: "date",
        required: false,
        attributeType: "patient",
        displayFormat: "yyyy",
        pastOnly: true
      },
      {
        id: "bpSystolic",
        label: "Systolic BP",
        type: "number",
        required: false,
        attributeType: "exam",
        min: 90,
        max: 220,
        keyboard: "numberPad"
      },
      {
        id: "HbA1c",
        label: "HbA1c (%)",
        type: "number",
        required: false,
        attributeType: "exam",
        min: 3,
        max: 12,
        keyboard: "decimalPad"
      }
    ];

    // Common regex patterns with user-friendly labels
    this.regexPatterns = [
      { value: "", display: "None (any text allowed)" },
      { value: "^[A-Za-z]+$", display: "Letters only" },
      { value: "^[0-9]+$", display: "Numbers only" },
      { value: "^[A-Za-z0-9]+$", display: "Letters and numbers only" },
      {
        value: "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$",
        display: "Email format"
      }
    ];

    this.state = {
      isLoaded: true,
      ...objsWithValues,
      customFields: customFields,
      showAddFieldForm: false,
      newField: {
        id: "",
        label: "",
        type: "text",
        required: false,
        placeholder: "",
        attributeType: "patient", // patient or exam
        options: [],
        // Text specific
        minLength: "",
        maxLength: "",
        regex: "",
        // Number specific
        min: "",
        max: "",
        // Date specific
        displayFormat: "",
        pastOnly: false,
        // Keyboard options
        keyboard: ""
      }
    };
    this.onSubmit = this.onSubmit.bind(this);
    this.onChange = this.onChange.bind(this);
    this.addCustomField = this.addCustomField.bind(this);
    this.removeCustomField = this.removeCustomField.bind(this);
    this.updateCustomField = this.updateCustomField.bind(this);
    this.toggleAddFieldForm = this.toggleAddFieldForm.bind(this);
    this.handleNewFieldChange = this.handleNewFieldChange.bind(this);
    this.saveNewField = this.saveNewField.bind(this);
    this.addFieldOption = this.addFieldOption.bind(this);
    this.removeFieldOption = this.removeFieldOption.bind(this);
    this.handleRetinaRiskToggle = this.handleRetinaRiskToggle.bind(this);
  }

  componentDidMount() {}

  // Helper function to ensure RETINA_RISK required fields are present
  ensureRetinaRiskFields(customFields) {
    // Ensure customFields is an array
    let updatedFields = Array.isArray(customFields) ? [...customFields] : [];

    this.retinaRiskRequiredFields.forEach(requiredField => {
      const existingFieldIndex = updatedFields.findIndex(
        field => field.id === requiredField.id
      );
      if (existingFieldIndex === -1) {
        // Field doesn't exist, add it
        updatedFields.push({ ...requiredField });
      } else {
        // Field exists, update it to match required specs
        updatedFields[existingFieldIndex] = { ...requiredField };
      }
    });

    return updatedFields;
  }

  // Helper function to remove RETINA_RISK required fields
  removeRetinaRiskFields(customFields) {
    // Ensure customFields is an array
    if (!Array.isArray(customFields)) {
      return [];
    }

    const requiredFieldIds = this.retinaRiskRequiredFields.map(
      field => field.id
    );
    return customFields.filter(field => !requiredFieldIds.includes(field.id));
  }

  handleRetinaRiskToggle(checked) {
    // Defensive check to ensure state exists
    if (!this.state) {
      return;
    }

    // Ensure customFields exists, default to empty array if undefined
    let updatedCustomFields =
      this.state.customFields && Array.isArray(this.state.customFields)
        ? [...this.state.customFields]
        : [];

    if (checked) {
      // Check if adding RETINA_RISK fields would exceed the limit
      const retinaFieldsToAdd = this.retinaRiskRequiredFields.filter(
        requiredField =>
          !updatedCustomFields.find(field => field.id === requiredField.id)
      );

      if (
        updatedCustomFields.length + retinaFieldsToAdd.length >
        this.MAX_CUSTOM_FIELDS
      ) {
        toast.error(
          `Cannot enable RETINA_RISK. Maximum ${this.MAX_CUSTOM_FIELDS} custom fields allowed. Please remove some fields first.`
        );
        return;
      }

      // Add required fields
      updatedCustomFields = this.ensureRetinaRiskFields(updatedCustomFields);
    } else {
      // Remove required fields
      updatedCustomFields = this.removeRetinaRiskFields(updatedCustomFields);
    }

    this.setState({
      RETINA_RISK: checked,
      customFields: updatedCustomFields
    });
  }

  // Helper function to get regex pattern display name
  getRegexDisplayName(regexValue) {
    const pattern = this.regexPatterns.find(p => p.value === regexValue);
    return pattern ? pattern.display : regexValue;
  }

  /**
   * Submits the form
   *
   * @param {object} e      event listener
   */
  onSubmit() {
    this.setState({ isLoaded: false });

    // Convert customFields back to JSON string for CUSTOM_FIELDS
    // Convert customFields with proper number types
    const customFieldsWithNumbers = this.state.customFields.map(field => ({
      ...field,
      // Convert string values to numbers for validation properties
      minLength: field.minLength ? Number(field.minLength) : undefined,
      maxLength: field.maxLength ? Number(field.maxLength) : undefined,
      min: field.min ? Number(field.min) : undefined,
      max: field.max ? Number(field.max) : undefined
    }));

    // Convert customFields back to JSON string for CUSTOM_FIELDS
    let stateWithCustomFields = { ...this.state };
    stateWithCustomFields.CUSTOM_FIELDS = JSON.stringify(
      customFieldsWithNumbers
    );

    let objs = Object.entries(this.props.siteEntitlements)
      .flatMap(item => item[1].flatMap(it => it.name))
      .map(i => {
        return { name: i, value: stateWithCustomFields[i] };
      })
      .filter(
        el =>
          el.value !== "" && el.value != null && typeof el.value !== "undefined"
      );

    putAuthenticatedData(
      sessionStorage.getItem("domain") +
        updateSiteConfigurations +
        "/" +
        this.props.siteId,
      objs
    )
      .then(result => {
        this.setState({ isLoaded: true });
        if (result.status === 400) {
          this.setState({ isLoaded: true, disableButton: false });
        } else if (result.status === 200) {
          toast.success(
            this.t(T_SITES.toast.siteConfigSettingsUpdatedSuccessfully)
          );
          this.props.closePopup();
          this.props.resetList();
        }
        return result.json();
      })
      .then(resp => {
        if (
          resp.status.statusCode !== "OK" &&
          resp.status.statusCode !== "FORBIDDEN" &&
          resp.status.statusCode !== "NOT_AUTHORIZED"
        ) {
          toast.error(resp.status.message);
        }
      });
  }

  onChange(e, type, charLimit) {
    if (
      (type === "STRING" && charLimit && e.target.value.length <= charLimit) ||
      (type !== "STRING" && type !== "INTEGER") ||
      (type === "STRING" && !charLimit)
    )
      this.setState({ [e.target.name]: e.target.value });
    if (
      type === "INTEGER" &&
      (/^[0-9\b]+$/.test(e.target.value) || e.target.value === "")
    ) {
      this.setState({
        [e.target.name]: e.target.value === "" ? "" : parseInt(e.target.value)
      });
    }
  }

  toggleAddFieldForm() {
    this.setState({
      showAddFieldForm: !this.state.showAddFieldForm,
      newField: {
        id: "",
        label: "",
        type: "text",
        required: false,
        placeholder: "",
        attributeType: "patient",
        options: [],
        minLength: "",
        maxLength: "",
        regex: "",
        min: "",
        max: "",
        displayFormat: "",
        pastOnly: false,
        keyboard: ""
      }
    });
  }

  handleNewFieldChange(e) {
    const { name, value, type, checked } = e.target;
    this.setState({
      newField: {
        ...this.state.newField,
        [name]: type === "checkbox" ? checked : value
      }
    });
  }

  addFieldOption() {
    this.setState({
      newField: {
        ...this.state.newField,
        options: [...this.state.newField.options, { value: "", display: "" }]
      }
    });
  }

  removeFieldOption(index) {
    const options = [...this.state.newField.options];
    options.splice(index, 1);
    this.setState({
      newField: {
        ...this.state.newField,
        options
      }
    });
  }

  updateFieldOption(index, field, value) {
    const options = [...this.state.newField.options];
    options[index][field] = value;
    this.setState({
      newField: {
        ...this.state.newField,
        options
      }
    });
  }

  saveNewField() {
    const { newField } = this.state;
    if (!newField.id || !newField.label) {
      toast.error("Field ID and Label are required");
      return;
    }

    // Check for duplicate IDs
    if (this.state.customFields.find(f => f.id === newField.id)) {
      toast.error("Field ID must be unique");
      return;
    }

    // Check if adding this field would exceed the limit
    if (this.state.customFields.length >= this.MAX_CUSTOM_FIELDS) {
      toast.error(
        `Maximum ${this.MAX_CUSTOM_FIELDS} custom fields allowed. Please remove a field before adding a new one.`
      );
      return;
    }

    const fieldToAdd = { ...newField };

    // Clean up the field based on type - remove irrelevant properties
    if (fieldToAdd.type !== "picker") {
      delete fieldToAdd.options;
    }
    if (fieldToAdd.type !== "text") {
      delete fieldToAdd.minLength;
      delete fieldToAdd.maxLength;
      delete fieldToAdd.regex;
    }
    if (fieldToAdd.type !== "number") {
      delete fieldToAdd.min;
      delete fieldToAdd.max;
    }
    if (fieldToAdd.type !== "date") {
      delete fieldToAdd.displayFormat;
      delete fieldToAdd.pastOnly;
    }

    // Remove empty string values to keep JSON clean
    Object.keys(fieldToAdd).forEach(key => {
      if (fieldToAdd[key] === "") {
        delete fieldToAdd[key];
      }
    });

    this.setState({
      customFields: [...this.state.customFields, fieldToAdd],
      showAddFieldForm: false,
      newField: {
        id: "",
        label: "",
        type: "text",
        required: false,
        placeholder: "",
        options: [],
        minLength: "",
        maxLength: "",
        regex: "",
        min: "",
        max: "",
        displayFormat: "",
        pastOnly: false,
        keyboard: ""
      }
    });
  }

  addCustomField() {
    this.toggleAddFieldForm();
  }

  removeCustomField(index) {
    // Ensure customFields exists and is an array
    if (!this.state.customFields || !Array.isArray(this.state.customFields)) {
      return;
    }

    const field = this.state.customFields[index];

    // Prevent removal of RETINA_RISK required fields when RETINA_RISK is enabled
    if (
      this.state.RETINA_RISK &&
      this.retinaRiskRequiredFields.some(rf => rf.id === field.id)
    ) {
      toast.error(
        "This field is required when RETINA_RISK is enabled and cannot be removed."
      );
      return;
    }

    const customFields = [...this.state.customFields];
    customFields.splice(index, 1);
    this.setState({ customFields });
  }

  updateCustomField(index, field, value) {
    const customFields = [...this.state.customFields];
    customFields[index][field] = value;
    this.setState({ customFields });
  }

  renderCustomFieldsSection() {
    return (
      <div>
        <h6>ADD EXAM CONFIGURATIONS</h6>

        {/* Existing custom fields */}
        {this.state.customFields.map((field, index) => (
          <div
            key={index}
            style={{
              border: "1px solid #ccc",
              padding: "10px",
              margin: "10px 0",
              borderRadius: "4px"
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}
            >
              <strong>
                {field.label} ({field.type})
              </strong>
              <Button
                size="sm"
                variant="danger"
                onClick={() => this.removeCustomField(index)}
                style={{
                  background: "#dc3545",
                  border: "none",
                  padding: "2px 8px"
                }}
              >
                Remove
              </Button>
            </div>
            <div style={{ fontSize: "12px", color: "#666", marginTop: "5px" }}>
              ID: {field.id} | Required: {field.required ? "Yes" : "No"} |
              Attribute: {field.attributeType || "patient"}
              {field.type === "text" &&
                (field.minLength || field.maxLength || field.regex) && (
                  <div>
                    Text: {field.minLength && `Min: ${field.minLength}`}{" "}
                    {field.maxLength && `Max: ${field.maxLength}`}{" "}
                    {field.regex &&
                      `Pattern: ${this.getRegexDisplayName(field.regex)}`}
                  </div>
                )}
              {field.type === "number" &&
                (field.min !== undefined || field.max !== undefined) && (
                  <div>
                    Number: {field.min !== undefined && `Min: ${field.min}`}{" "}
                    {field.max !== undefined && `Max: ${field.max}`}
                  </div>
                )}
              {field.type === "date" &&
                (field.displayFormat || field.pastOnly) && (
                  <div>
                    Date:{" "}
                    {field.displayFormat && `Format: ${field.displayFormat}`}{" "}
                    {field.pastOnly && "Past Only"}
                  </div>
                )}
              {field.keyboard && <div>Keyboard: {field.keyboard}</div>}
              {field.options && field.options.length > 0 && (
                <div>
                  Options: {field.options.map(opt => opt.display).join(", ")}
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Add new field form */}
        {this.state.showAddFieldForm && (
          <div
            style={{
              border: "2px solid #007bff",
              padding: "15px",
              margin: "10px 0",
              borderRadius: "4px",
              background: "#f8f9fa"
            }}
          >
            <h6>Add New Field</h6>

            <FormGroup>
              <div className="row formfont">
                <div className="col-md-3 formfont">ATTRIBUTE TYPE</div>
                <div className="col-md-9">
                  <select
                    name="attributeType"
                    value={this.state.newField.attributeType}
                    onChange={this.handleNewFieldChange}
                    style={{ width: "100%", padding: "8px" }}
                  >
                    <option value="patient">Patient Attribute</option>
                    <option value="exam">Exam Attribute</option>
                  </select>
                  <small
                    style={{
                      color: "#666",
                      fontSize: "11px",
                      marginTop: "5px",
                      display: "block"
                    }}
                  >
                    Choose whether this field applies to patient information or
                    exam-specific data
                  </small>
                </div>
              </div>
            </FormGroup>

            <FormGroup>
              <div className="row formfont">
                <div className="col-md-3 formfont">LABEL</div>
                <div className="col-md-9">
                  <FormControl
                    type="text"
                    name="label"
                    value={this.state.newField.label}
                    onChange={this.handleNewFieldChange}
                    placeholder="e.g. First Name, Age etc."
                  />
                </div>
              </div>
            </FormGroup>

            <FormGroup>
              <div className="row formfont">
                <div className="col-md-3 formfont">PLACEHOLDER</div>
                <div className="col-md-9">
                  <FormControl
                    type="text"
                    name="placeholder"
                    value={this.state.newField.placeholder || ""}
                    onChange={this.handleNewFieldChange}
                    placeholder="e.g. Antonio Garcia"
                  />
                </div>
              </div>
            </FormGroup>

            <FormGroup>
              <div className="row formfont">
                <div className="col-md-3 formfont">TYPE OF INPUT</div>
                <div className="col-md-9">
                  <select
                    name="type"
                    value={this.state.newField.type}
                    onChange={this.handleNewFieldChange}
                    style={{ width: "100%", padding: "8px" }}
                  >
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                    <option value="date">Date</option>
                    <option value="picker">Drop Down Picker</option>
                  </select>
                </div>
              </div>
            </FormGroup>

            {this.state.newField.type === "picker" && (
              <FormGroup>
                <div className="row formfont">
                  <div className="col-md-3 formfont">
                    Drop Down Picker Options
                  </div>
                  <div className="col-md-9">
                    {this.state.newField.options.map((option, optIndex) => (
                      <div
                        key={optIndex}
                        style={{
                          display: "flex",
                          marginBottom: "5px",
                          gap: "10px"
                        }}
                      >
                        <FormControl
                          type="text"
                          placeholder="Value (e.g. Type1)"
                          value={option.value}
                          onChange={e =>
                            this.updateFieldOption(
                              optIndex,
                              "value",
                              e.target.value
                            )
                          }
                          style={{ flex: 1 }}
                        />
                        <FormControl
                          type="text"
                          placeholder="Display (e.g. Type 1)"
                          value={option.display}
                          onChange={e =>
                            this.updateFieldOption(
                              optIndex,
                              "display",
                              e.target.value
                            )
                          }
                          style={{ flex: 1 }}
                        />
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => this.removeFieldOption(optIndex)}
                          style={{ background: "#dc3545", border: "none" }}
                        >
                          -
                        </Button>
                      </div>
                    ))}
                    <Button
                      size="sm"
                      onClick={this.addFieldOption}
                      style={{
                        background: "#28a745",
                        border: "none",
                        color: "white"
                      }}
                    >
                      + Add Option
                    </Button>
                  </div>
                </div>
              </FormGroup>
            )}

            {/* Text-specific fields */}
            {this.state.newField.type === "text" && (
              <>
                <FormGroup>
                  <div className="row formfont">
                    <div className="col-md-3 formfont">Min Length</div>
                    <div className="col-md-9">
                      <FormControl
                        type="number"
                        name="minLength"
                        value={this.state.newField.minLength}
                        onChange={this.handleNewFieldChange}
                        placeholder="Minimum character length"
                      />
                    </div>
                  </div>
                </FormGroup>

                <FormGroup>
                  <div className="row formfont">
                    <div className="col-md-3 formfont">Max Length</div>
                    <div className="col-md-9">
                      <FormControl
                        type="number"
                        name="maxLength"
                        value={this.state.newField.maxLength}
                        onChange={this.handleNewFieldChange}
                        placeholder="Maximum character length"
                      />
                    </div>
                  </div>
                </FormGroup>

                <FormGroup>
                  <div className="row formfont">
                    <div className="col-md-3 formfont">Text Validation</div>
                    <div className="col-md-9">
                      <select
                        name="regex"
                        value={this.state.newField.regex}
                        onChange={this.handleNewFieldChange}
                        style={{ width: "100%", padding: "8px" }}
                      >
                        {this.regexPatterns.map((pattern, index) => (
                          <option key={index} value={pattern.value}>
                            {pattern.display}
                          </option>
                        ))}
                      </select>
                      <small
                        style={{
                          color: "#666",
                          fontSize: "11px",
                          marginTop: "5px",
                          display: "block"
                        }}
                      >
                        Choose what type of text is allowed in this field
                      </small>
                    </div>
                  </div>
                </FormGroup>

                <FormGroup>
                  <div className="row formfont">
                    <div className="col-md-3 formfont">Keyboard Type</div>
                    <div className="col-md-9">
                      <select
                        name="keyboard"
                        value={this.state.newField.keyboard}
                        onChange={this.handleNewFieldChange}
                        style={{ width: "100%", padding: "8px" }}
                      >
                        <option value="">Default</option>
                        <option value="alphabet">Alphabet</option>
                        <option value="alphanumeric">Alphanumeric</option>
                      </select>
                    </div>
                  </div>
                </FormGroup>
              </>
            )}

            {/* Number-specific fields */}
            {this.state.newField.type === "number" && (
              <>
                <FormGroup>
                  <div className="row formfont">
                    <div className="col-md-3 formfont">Minimum Value</div>
                    <div className="col-md-9">
                      <FormControl
                        type="number"
                        name="min"
                        value={this.state.newField.min}
                        onChange={this.handleNewFieldChange}
                        placeholder="Minimum allowed value"
                      />
                    </div>
                  </div>
                </FormGroup>

                <FormGroup>
                  <div className="row formfont">
                    <div className="col-md-3 formfont">Maximum Value</div>
                    <div className="col-md-9">
                      <FormControl
                        type="number"
                        name="max"
                        value={this.state.newField.max}
                        onChange={this.handleNewFieldChange}
                        placeholder="Maximum allowed value"
                      />
                    </div>
                  </div>
                </FormGroup>

                <FormGroup>
                  <div className="row formfont">
                    <div className="col-md-3 formfont">Keyboard Type</div>
                    <div className="col-md-9">
                      <select
                        name="keyboard"
                        value={this.state.newField.keyboard}
                        onChange={this.handleNewFieldChange}
                        style={{ width: "100%", padding: "8px" }}
                      >
                        <option value="">Default</option>
                        <option value="numberPad">Number Pad</option>
                        <option value="decimalPad">Decimal Pad</option>
                      </select>
                    </div>
                  </div>
                </FormGroup>
              </>
            )}

            {/* Date-specific fields */}
            {this.state.newField.type === "date" && (
              <>
                <FormGroup>
                  <div className="row formfont">
                    <div className="col-md-3 formfont">Display Format</div>
                    <div className="col-md-9">
                      <select
                        name="displayFormat"
                        value={this.state.newField.displayFormat}
                        onChange={this.handleNewFieldChange}
                        style={{ width: "100%", padding: "8px" }}
                      >
                        <option value="">Select format</option>
                        <option value="yyyy">Year only (yyyy)</option>
                        <option value="MM/yyyy">Month/Year (MM/yyyy)</option>
                        <option value="dd/MM/yyyy">
                          Full Date (dd/MM/yyyy)
                        </option>
                        <option value="yyyy-MM-dd">
                          ISO Date (yyyy-MM-dd)
                        </option>
                      </select>
                    </div>
                  </div>
                </FormGroup>

                <FormGroup>
                  <div className="row formfont">
                    <div className="col-md-3 formfont">Past Only</div>
                    <div className="col-md-9">
                      <BootstrapSwitchButton
                        width={100}
                        height={10}
                        checked={this.state.newField.pastOnly}
                        onChange={checked =>
                          this.setState({
                            newField: {
                              ...this.state.newField,
                              pastOnly: checked
                            }
                          })
                        }
                        onstyle="success"
                      />
                      <small style={{ marginLeft: "10px", color: "#666" }}>
                        Only allow past dates
                      </small>
                    </div>
                  </div>
                </FormGroup>
              </>
            )}

            <FormGroup>
              <div className="row formfont">
                <div className="col-md-3 formfont">MARK AS MANDATORY</div>
                <div className="col-md-9">
                  <BootstrapSwitchButton
                    width={100}
                    height={10}
                    checked={this.state.newField.required}
                    onChange={checked =>
                      this.setState({
                        newField: { ...this.state.newField, required: checked }
                      })
                    }
                    onstyle="success"
                  />
                </div>
              </div>
            </FormGroup>

            <FormGroup>
              <div className="row formfont">
                <div className="col-md-3 formfont">Field ID</div>
                <div className="col-md-9">
                  <FormControl
                    type="text"
                    name="id"
                    value={this.state.newField.id}
                    onChange={this.handleNewFieldChange}
                    placeholder="Unique identifier (e.g. firstName, diabetesType)"
                  />
                </div>
              </div>
            </FormGroup>

            <div style={{ textAlign: "right", marginTop: "10px" }}>
              <Button
                onClick={this.toggleAddFieldForm}
                style={{
                  marginRight: "10px",
                  background: "#6c757d",
                  border: "none"
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={this.saveNewField}
                style={{ background: "#007bff", border: "none" }}
              >
                Add Field
              </Button>
            </div>
          </div>
        )}

        {/* Add field button */}
        {!this.state.showAddFieldForm && (
          <div style={{ textAlign: "center", margin: "15px 0" }}>
            <Button
              onClick={this.addCustomField}
              disabled={
                this.state.customFields.length >= this.MAX_CUSTOM_FIELDS
              }
              style={{
                background:
                  this.state.customFields.length >= this.MAX_CUSTOM_FIELDS
                    ? "#6c757d"
                    : "#28a745",
                border: "none",
                color: "white"
              }}
            >
              + Add New Field
            </Button>
            {this.state.customFields.length >= this.MAX_CUSTOM_FIELDS && (
              <div
                style={{ color: "#dc3545", fontSize: "12px", marginTop: "5px" }}
              >
                Maximum {this.MAX_CUSTOM_FIELDS} custom fields allowed
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  handleSubmit(event) {
    event.preventDefault();
  }

  render() {
    return (
      <div className="custommodal">
        <div className="custommodalinner site-settings">
          <form className="loginpad" onSubmit={this.handleSubmit}>
            <h6 className="modal-heading">
              {this.t(T_SERVICE.viewSites.siteConfigurations.header)}
            </h6>
            <Button onClick={this.props.closePopup} className="cancle">
              {" "}
              x{" "}
            </Button>
            <hr className="hrpadding" />
            <div className="firstpage">
              {(() => {
                return Object.entries(this.props.siteEntitlements)
                  ?.map(([name, obj]) => ({ name, ...obj }))
                  ?.map(item => {
                    return (
                      <React.Fragment key={item.name}>
                        {item.name === "CUSTOM_FIELDS" ? (
                          this.renderCustomFieldsSection()
                        ) : (
                          <>
                            <h6>{item.name}</h6>
                            {(() => {
                              return Object.assign([], item)?.map(formItem => {
                                return (
                                  <FormGroup
                                    key={formItem.name}
                                    controlId="email"
                                    bsSize="large"
                                  >
                                    <div className="row formfont">
                                      <div className="col-md-6 formfont enable-label">
                                        {formItem.name}
                                      </div>
                                      <div className="col-md-6">
                                        {(() => {
                                          switch (formItem.type) {
                                            case "STRING":
                                            case "INTEGER":
                                              return (
                                                <FormControl
                                                  type="text"
                                                  name={formItem.name}
                                                  value={
                                                    this.state[formItem.name]
                                                  }
                                                  onChange={e =>
                                                    this.onChange(
                                                      e,
                                                      formItem.type,
                                                      formItem.charLimit
                                                    )
                                                  }
                                                />
                                              );
                                            case "BOOLEAN":
                                              // Special handling for RETINA_RISK
                                              if (
                                                formItem.name === "RETINA_RISK"
                                              ) {
                                                return (
                                                  <BootstrapSwitchButton
                                                    width={100}
                                                    height={10}
                                                    checked={
                                                      this.state[
                                                        formItem.name
                                                      ] || false
                                                    }
                                                    onChange={checked =>
                                                      this.handleRetinaRiskToggle(
                                                        checked
                                                      )
                                                    }
                                                    onstyle="success"
                                                  />
                                                );
                                              } else {
                                                return (
                                                  <BootstrapSwitchButton
                                                    width={100}
                                                    height={10}
                                                    checked={
                                                      this.state[formItem.name]
                                                    }
                                                    onChange={checked => {
                                                      this.setState({
                                                        [formItem.name]: checked
                                                      });
                                                    }}
                                                    onstyle="success"
                                                  />
                                                );
                                              }
                                            case "ENUM":
                                              return (
                                                <select
                                                  name={formItem.name}
                                                  value={
                                                    this.state[formItem.name]
                                                  }
                                                  onChange={e =>
                                                    this.onChange(
                                                      e,
                                                      formItem.type
                                                    )
                                                  }
                                                >
                                                  <option key={-1} value="">
                                                    {this.t(
                                                      T_GENERAL.selectAnOption
                                                    )}
                                                  </option>
                                                  {(() => {
                                                    return formItem.possibleValues.map(
                                                      (ddItem, key) => (
                                                        <option
                                                          key={key}
                                                          value={ddItem}
                                                        >
                                                          {ddItem}
                                                        </option>
                                                      )
                                                    );
                                                  })()}
                                                </select>
                                              );
                                          }
                                        })()}
                                      </div>
                                    </div>
                                  </FormGroup>
                                );
                              });
                            })()}
                          </>
                        )}
                      </React.Fragment>
                    );
                  });
              })()}

              <Button
                onClick={this.onSubmit}
                className="loginbtn1 site-settings-btn submit-button"
              >
                {this.state.isLoaded ? (
                  this.t(T_GENERAL.submit)
                ) : (
                  <img
                    src={require("../../assets/images/button-loading.gif")}
                  />
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  }
}

export default withTranslation()(CustomSiteSettingsModal);
